import type { BrandConfig } from "./types";

export const novaBrand: BrandConfig = {
  name: "nova",
  siteTitle: "Quantum Young Member",

  // Primitive color tokens reused across multiple features.
  colors: {
    // Global neutrals and transparent keyword.
    common: {
      white: "#FFFFFF",
      black: "#000000",
      transparent: "transparent",
    },

    // Brand identity, gradients, and accent fills.
    brand: {
      secondary: "#625B71",
      gradientStart: "#2AACE2",
      gradientMiddle: "#91288D",
      gradientAccent: "#E72B45",
      gradientWarm: "#F18230",
      gradientEnd: "#FFC82C",
      accentBronze: "#8C7958",
      accentCritical: "#FF0000",
      accentCriticalStrong: "#FF0011",
      pointsAccent: "#FFA300",
      pointsIcon: "#FFA402",
      pointsGradientStart: "#FED467",
      pointsGradientMid: "#FFDA3E",
      pointsGradientEnd: "#FFF8C7",
    },

    // Generic background surfaces and feedback fills.
    background: {
      appSecondary: "#222221",
      pagePrimary: "#FFFFFF",
      surfaceSubtle: "#FCFCFC",
      fieldPrimary: "#FFF9F2",
      feedbackSuccess: "#F5FEE9",
      feedbackWarning: "#FDF5E8",
      feedbackNeutral: "#F1F1F1",
      feedbackFreeze: "#E3F2FD",
    },

    // Generic text primitives.
    text: {
      onDarkPlaceholder: "#FFFFFF",
      onLightSecondary: "rgba(0, 0, 0, 0.6)",
      primary: "#1F1F1F",
      primaryAlt: "#1C1B1C",
      primaryStrong: "#181817",
      tertiary: "#8E8A86",
      tertiaryAlt: "#8E8E93",
      disabled: "#797677",
      emphasis: "rgba(0, 0, 0, 0.87)",
      disabledOnLight: "rgba(0, 0, 0, 0.38)",
    },

    // Shared border and outline colors.
    border: {
      secondary: "#CFCFCA",
      divider: "#857371",
      strong: "#5B5A59",
      muted: "#C6C6C6",
      neutral: "#BDBDBD",
      iconSurface: "#E0E0E0",
    },

    // Semantic status colors.
    state: {
      error: "#BA1A1A",
      success: "#2E7D32",
      successBright: "#33B15B",
      warning: "#F18230",
      warningAlt: "#ED6C02",
      neutral: "#9E9E9E",
      freeze: "#2AACE2",
    },

    // Overlay colors for disabled, video, and translucent UI states.
    overlay: {
      disabled: "rgba(0, 0, 0, 0.12)",
      video: "rgba(0,0,0,0.35)",
      videoEnd: "rgba(0,0,0,0.45)",
      onDarkSoft: "rgba(255, 255, 255, 0.1)",
      onDarkDisabled: "rgba(255, 255, 255, 0.16)",
      mutedLight: "rgba(198, 198, 198, 0.12)",
    },

    // Shared icon fills.
    icon: {
      badgeBackground: "#EEE1CA",
      badgeForeground: "#484647",
      poolForeground: "#1C1B1C",
      targetForeground: "#C7C6C6",
    },
  },

  // Non-trivial gradient strings.
  gradients: {
    videoOverlay:
      "linear-gradient(180deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.45) 100%)",
  },

  // Authentication page composition.
  auth: {
    loginHeader: {
      title: "Log in",
      showDivider: false,
    },
    footer: {
      logo: "app",
      text: "Powered by FitUp",
    },
  },

  // Member area screen-specific tokens.
  member: {
    secondaryCardBackground: "#222221",
    headerBackground: "#1C1B1C",
    headerPrimaryText: "#FFFFFF",
    headerSecondaryText: "rgba(255, 255, 255, 0.16)",
    sectionTitleColor: "#FFFFFF",
    sectionDividerColor: "#C7C6C6",
    emptyStateText: "#E5E1E1",
    pointsPage: {
      headerBackground: "#1C1B1C",
      headerBackgroundImage: "point-background",
      headerText: "#FFFFFF",
      filterIcon: "#FFFFFF",
      monthText: "#FFFFFF",
      transactionDate: "#E5E1E1",
      transactionDivider: "#857371",
    },
    qrSection: {
      cardBackground: "#393939",
      iconBackground: "#8C7958",
      iconColor: "#FFFFFF",
      labelColor: "#C7C6C6",
    },
    goalSetting: {
      dateActiveBackground: "#CFCFCA",
      dateActiveBorder: "#CFCFCA",
      dateActiveText: "#181817",
      dateInactiveBackground: "transparent",
      dateInactiveBorder: "#CFCFCA",
      dateInactiveText: "#EEE1CA",
      cardBackground: "#393939",
      cardTitle: "#FFFFFF",
      cardText: "#EEE1CA",
      cardMutedText: "#C7C6C6",
      cardDivider: "#C7C6C6",
      cardActionText: "#C7C6C6",
      iconBackground: "#EEE1CA",
      iconForeground: "#484647",
      targetIcon: "#C7C6C6",
      chartLine: "#FFFFFF",
      chartLabel: "#FFFFFF",
      chartGrid: "#FFFFFF",
      sectionAccent: "#8C7958",
    },
  },

  booking: {
    timeSlot: {
      titleColor: "#FFFFFF",
      borderColor: "#C6C6C6",
      textColor: "#C6C6C6",
      selectedBackground: "#CFCFCA",
      selectedTextColor: "#181817",
      hoverBackground: "rgba(198, 198, 198, 0.12)",
    },
  },

  facility: {
    booking: {
      paymentSlip: {
        titleColor: "#EEE1CA",
      },
    },
  },

  // Consumer-facing palette consumed by COLORS, theme, and feature styles.
  palette: {
    // Core brand and shell tokens.
    primary: "#1C1B1C",
    primaryLight: "#DCDCDC",
    bodyBackground: "#1C1B1C",
    gradient:
      "linear-gradient(270deg, #2AACE2 0%, #91288D 25%, #E72B45 50%, #F18230 75%, #FFC82C 100%)",
    loginBackground:
      "linear-gradient(13.79deg, #2AACE2 5.09%, #91288D 28.76%, #E72B45 50.29%, #F18230 73.1%, #FFC82C 94.63%)",

    // Card surfaces and on-card text.
    cardPrimary: "#484647",
    cardSurface: "#393939",
    upcomingChipBackground: "#CFCFCA",
    cardText: "#EEE1CA",
    cardTitle: "#FFFFFF",
    cardTextMuted: "#C7C6C6",
    cardTextDim: "#979797",
    cardDivider: "#C7C6C6",

    // Page-level typography.
    textPrimary: "#FFFFFF",
    scheduleSectionMonth: "#FFFFFF",
    textSecondary: "#534341",
    readMoreButtonColor: "#C7C6C6",

    // Shared accents and filters.
    accentActive: "#837958",

    filterActiveBg: "#C7C6C6",
    datePickerBackground: "#F1E7D9",
    selectOptionBackground: "#F1E7D9",
    filterActiveColor: "#000000",
    filterInactiveBg: "transparent",
    filterInactiveColor: "#C7C6C6",
    filterInactiveBorder: "1px solid #C7C6C6",
    filterActiveBorder: "1px solid #C7C6C6",
    trainerFilterActiveBg: "#DCDCDC",
    trainerFilterActiveColor: "#1F1F1F",
    trainerFilterActiveHoverBg: "#DCDCDC",
    trainerFilterActiveBorder: "1px solid #FFFFFF",
    trainerFilterInactiveBg: "transparent",
    trainerFilterInactiveColor: "#FFFFFF",
    trainerFilterInactiveHoverBg: "rgba(255, 255, 255, 0.1)",
    trainerFilterInactiveBorder: "1px solid #FFFFFF",

    // Stepper state colors.
    stepperActive: "#8C7958",
    stepperInactive: "#CFCFCA",

    // Page-muted text and brand logo fill.
    pageTextMuted: "#C7C6C6",
    logoFill: "#DE2826",

    // Tabs and date buttons.
    tabInactiveColor: "#C7C6C6",
    tabActiveColor: "#FFFFFF",
    dateButtonSelectedText: "#1C1B1C",

    // Empty-state and sheet/dialog tokens.
    emptyStateIconColor: "#E0E0E0",

    sheetBackground: "#1C1B1C",
    sheetText: "#FFFFFF",
    reservationDateRangeIcon: "#1C1B1C",
    dialogText: "#000000",
    dialogTextMuted: "#979797",
    dialogPointIcon: "#484647",
    fabBackground: "#1C1B1C",
    fabBackgroundHover: "#1C1B1C",
    errorLight: "#C74C3F",
  },

  // Shared shadows.
  shadows: {
    card: "0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
    bottomSheet: "0px -12px 40px rgba(0, 0, 0, 0.5)",
    cardSubtle: "0px 1px 2px rgba(0, 0, 0, 0.04)",
    button: "0px 4px 12px rgba(0,0,0,0.25)",
    buttonHover: "0px 6px 16px rgba(0,0,0,0.3)",
    dialog: "0px 12px 32px rgba(0, 0, 0, 0.18)",
    dialogHover: "0px 6px 14px rgba(0, 0, 0, 0.25)",
    videoPlay: "0 8px 16px rgba(0,0,0,0.35)",
  },

  // Shared button system tokens.
  button: {
    surface: "gradient",
    backgroundColor: "#1C1B1C",
    color: "#FFFFFF",
    hoverBackgroundColor: "#DCDCDC",
    hoverColor: "#1C1B1C",
    disabled: {
      backgroundColor: "rgba(255, 255, 255, 0.16)",
      color: "rgba(255, 255, 255, 0.38)",
    },
    secondary: {
      backgroundColor: "#FFFFFF",
      color: "#1F1F1F",
      borderColor: "#BDBDBD",
      hoverBackgroundColor: "#FFFFFF",
      hoverColor: "#1F1F1F",
      hoverBorderColor: "#BDBDBD",
    },
  },
};
